/**
 * The Export chrome — one button per mount, the undo-chrome shape (ADR-006
 * §13.0.1): it holds NO truth. What gets exported, when the scene is restable,
 * and how the outcome is announced all belong to the door callback main wires
 * in; the chrome only renders "idle vs running" so a slow write cannot be
 * double-fired. main mounts it twice: the model door (default labels) and the
 * animation-only clips door (delivery contract Artifact B). DOM only, no
 * engine. Depends on: nothing. Depended on by: main.
 */

export interface MountedExportChrome {
  dispose(): void;
}

export interface ExportChromeLabels {
  /** Button text, e.g. '⇩ Export GLB'. */
  readonly label: string;
  readonly ariaLabel: string;
}

const MODEL_EXPORT_LABELS: ExportChromeLabels = {
  label: '⇩ Export GLB',
  ariaLabel: 'Export the model with its animations as a GLB file',
};

export function mountExportChrome(
  root: HTMLElement,
  onExport: () => Promise<void>,
  labels: ExportChromeLabels = MODEL_EXPORT_LABELS,
): MountedExportChrome {
  // No bar and no fixed positioning of its own: the button joins an EXISTING
  // toolbar (main passes the shell chrome), so it can never sit on top of a
  // workspace zone — a fixed top-right mount covered the hands panel (Allen,
  // 2026-07-27).
  const divider = document.createElement('span');
  divider.style.cssText = 'width: 1px; height: 18px; background: var(--accent-border);';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = labels.label;
  btn.setAttribute('aria-label', labels.ariaLabel);
  btn.style.cssText =
    'font: inherit; padding: 4px 10px; border-radius: 6px; cursor: pointer; ' +
    'background: var(--accent-soft); color: var(--accent-text); ' +
    'border: 1px solid var(--accent-border);';

  let running = false;
  btn.addEventListener('click', () => {
    if (running) return;
    running = true;
    btn.disabled = true;
    btn.textContent = 'Exporting…';
    void onExport().finally(() => {
      running = false;
      btn.disabled = false;
      btn.textContent = labels.label;
    });
  });

  root.append(divider, btn);
  return {
    dispose() {
      divider.remove();
      btn.remove();
    },
  };
}
